import {useCallback, useState} from "react";
import {useScript} from "../hooks/useScript.hook";

const recaptchaKeyV3 = '6Ldk-vsUAAAAAPemRVUvWZuUUMK5xq_YtjfrcBP7';

declare const grecaptcha: {
    execute: (key: string, params: {action: string}) => Promise<string>;
    render: (key: string, params: {sitekey: string, callback: any}) => Promise<string>;
    reset: (widgetId?: string) => void;
};

export function useCaptchaV3(): (action: string) => Promise<string> {
    useScript(`https://www.google.com/recaptcha/api.js?render=${recaptchaKeyV3}`);
    return useCallback((action: string) => grecaptcha.execute(recaptchaKeyV3, {action}), []);
}

const captchaErrorCode = 401;
type WhatIWant = (tokenV3: string, actionName: string, v2Token: string) => Promise<void>;

export type HumanValidatedMethod = ReturnType<typeof useHumanValidation>;

export function useHumanValidation(onSuccess: () => void, actionName: string, onV3Fail: () => void, v2Token: string, disableSubmit: (value: boolean) => void, setShowError: (value: boolean) => void) {
    const executeCaptcha = useCaptchaV3();
    const [tokenV3, setTokenV3] = useState('');
    return useCallback((func: WhatIWant) => {
        disableSubmit(true);
        if (v2Token) {
            return func(tokenV3, actionName, v2Token).then(() => {
                onSuccess();
            }).catch(err => {
                disableSubmit(true);
                setShowError(true);
                grecaptcha.reset();
                const message = err.response.data.error.message as string;
                return {error: message.split(':', 2)[1]};
            });
        } else {
            return executeCaptcha(actionName).then(function (token: string) {
                setTokenV3(token);
                return func(token, actionName, v2Token).then(() => {
                    onSuccess();
                }).catch(err => {
                    if (err.response.data.error.code === captchaErrorCode) {
                        onV3Fail();
                    } else {
                        disableSubmit(false);
                    }
                    const message = err.response.data.error.message as string;
                    return {error: message.split(':', 2)[1]};
                });
            });
        }
    }, [disableSubmit, v2Token, tokenV3, actionName, onSuccess, setShowError, executeCaptcha, onV3Fail]);
}